import { api } from './api.js';

/**
 * §11: Push auf iOS ist ein Minenfeld mit drei Bedingungen, die alle gelten müssen:
 *
 *  1. Die App muss **vom Homebildschirm** laufen (`display-mode: standalone`). Im
 *     Safari-Tab existiert `PushManager` schlicht nicht.
 *  2. Die Erlaubnis darf nur nach einer echten Nutzergeste angefragt werden, sonst
 *     lehnt Safari sie stumm ab — und ein einmal abgelehntes „denied" lässt sich aus
 *     der App heraus nie wieder öffnen.
 *  3. Der Service Worker muss registriert und aktiv sein.
 *
 * Deshalb meldet dieses Modul jeden dieser Fälle mit eigenem Grund zurück, statt nur
 * „hat nicht geklappt" zu sagen. Ein Push, der still scheitert, ist die Fehlerklasse,
 * die in PLAN.md §5 ausdrücklich als Risiko steht.
 */

export type PushBlocker =
  | 'unsupported'
  | 'not-installed'
  | 'denied'
  | 'server-not-configured'
  | null;

export interface PushCapability {
  supported: boolean;
  installed: boolean;
  permission: NotificationPermission | 'unavailable';
  blocker: PushBlocker;
}

export function isStandalone(): boolean {
  // `navigator.standalone` ist die iOS-eigene Variante; der Media-Query deckt den Rest ab.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

export function capability(serverConfigured: boolean): PushCapability {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const installed = isStandalone();
  const permission = supported ? Notification.permission : ('unavailable' as const);

  const blocker: PushBlocker = !supported
    ? 'unsupported'
    : !installed
      ? 'not-installed'
      : permission === 'denied'
        ? 'denied'
        : !serverConfigured
          ? 'server-not-configured'
          : null;

  return { supported, installed, permission, blocker };
}

/** VAPID-Schlüssel kommen base64url-codiert; `PushManager` will rohe Bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  // Explizit über einen ArrayBuffer: `applicationServerKey` verlangt eine BufferSource
  // mit genau diesem Puffertyp.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePush(publicKey: string): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'Kein Service Worker verfügbar.' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return {
      ok: false,
      reason:
        permission === 'denied'
          ? 'Erlaubnis abgelehnt. Das lässt sich nur in den iOS-Einstellungen wieder ändern.'
          : 'Erlaubnis nicht erteilt.',
    };
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Ohne diese Zusage verweigern alle Browser das Abo: jede Nachricht muss dem
      // Nutzer sichtbar angezeigt werden. Das passt ohnehin zur Absicht.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await api.push.subscribe(subscription.toJSON());
  return { ok: true };
}

export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription === null) return;
  await api.push.unsubscribe(subscription.endpoint);
  await subscription.unsubscribe();
}

export function blockerText(blocker: PushBlocker): string {
  switch (blocker) {
    case 'unsupported':
      return 'Dieser Browser kann keine Web-Push-Nachrichten empfangen.';
    case 'not-installed':
      return 'Auf dem iPhone funktioniert Push nur, wenn die App über „Teilen → Zum Home-Bildschirm" installiert und von dort gestartet wurde.';
    case 'denied':
      return 'Die Erlaubnis wurde abgelehnt. Zurücknehmen lässt sie sich nur in den Systemeinstellungen unter Mitteilungen.';
    case 'server-not-configured':
      return 'Auf dem Server fehlen die VAPID-Schlüssel — siehe DEPLOY.md, Abschnitt 8.';
    case null:
      return '';
  }
}
